"use client";

/**
 * LoggedOutHeaderContent
 * ──────────────────────
 * Fills the header bar with a scrolling strip of square thumbnails
 * (artists, songs/releases, events) when no user is signed in.
 * Clicking any thumbnail navigates to the relevant detail page.
 * Matches the look of the logged-in FavoritesBar: square images, no text.
 */

import { useEffect, useState, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { HeaderThumbnailSkeleton } from './header-thumbnail-skeleton';

function getInitials(label: string): string {
  const words = label.trim().split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  if (words[0].length >= 2) return words[0].slice(0, 2).toUpperCase();
  return words[0][0]?.toUpperCase() || '?';
}
function stringToHue(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return Math.abs(hash) % 360;
}
import { useScrollDrag } from '@/hooks/use-scroll-drag';
import {
  getFeaturedArtists,
  getVisibleReleases,
  getUpcomingEvents,
} from '@/services/home';

interface Chip {
  id: string;
  label: string;
  imageUrl?: string;
  href: string;
  type: 'artist' | 'release' | 'event';
}

/** Fisher-Yates shuffle */
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function LoggedOutHeaderContent() {
  const [chips, setChips] = useState<Chip[]>([]);
  const [loaded, setLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useScrollDrag(containerRef);

  useEffect(() => {
    let cancelled = false;

    const publish = (next: Chip[]) => {
      // No cap: every release shows (thumbnails lazy-load as the strip scrolls).
      if (!cancelled && next.length) setChips(previous => shuffle([...previous, ...next]));
    };
    // Show the first available source instead of waiting for all three.
    void Promise.allSettled([
      getFeaturedArtists().then(artists => publish(artists.map(a => ({
        id: `artist-${a.id}`, label: a.name, imageUrl: a.imageUrl,
        href: a.profileLink || `/artists/${a.id}`, type: 'artist' as const,
      })))),
      getVisibleReleases().then(releases => publish(releases.map(r => ({
        id: `release-${r.id}`, label: r.title, imageUrl: r.imageUrl,
        href: `/music/${r.id}`, type: 'release' as const,
      })))),
      getUpcomingEvents().then(events => publish(events.map(e => ({
        id: `event-${e.id}`, label: e.title, imageUrl: e.imageUrl,
        href: `/events/${e.id}`, type: 'event' as const,
      })))),
    ]).finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  // Auto-scroll hint: once chips load, slowly scroll right then back to show it's scrollable
  useEffect(() => {
    const el = containerRef.current;
    if (!el || chips.length === 0) return;
    const t = setTimeout(() => {
      if (el.scrollWidth <= el.clientWidth) return;
      el.scrollTo({ left: 160, behavior: 'smooth' });
      const back = setTimeout(() => el.scrollTo({ left: 0, behavior: 'smooth' }), 3000);
      return () => clearTimeout(back);
    }, 1500);
    return () => clearTimeout(t);
  }, [chips.length]); // run once when chips first populate

  if (chips.length === 0) return loaded ? null : <HeaderThumbnailSkeleton />;

  return (
    <div
      ref={containerRef}
      className="flex h-full items-center overflow-x-auto hide-scrollbar drag-scroll gap-px"
    >
      {chips.map((chip) => {
        return (
          <Link
            key={chip.id}
            href={chip.href}
            className={cn(
              "bb-header-thumbnail relative block size-[var(--header-height)] flex-shrink-0 overflow-hidden",
              "hover:opacity-80 transition-opacity",
              "focus:outline-none focus:ring-1 focus:ring-primary",
            )}
            draggable={false}
            title={chip.label}
          >
            {chip.imageUrl ? (
              <Image
                src={chip.imageUrl}
                alt={chip.label}
                fill
                className="object-cover"
                sizes="(max-width: 389px) 44px, 50px"
              />
            ) : (
              (() => {
                const hue = stringToHue(chip.label);
                return (
                  <span
                    className="absolute inset-0 flex items-center justify-center select-none"
                    style={{ background: `hsl(${hue},45%,28%)` }}
                  >
                    <span
                      className="font-bold leading-none"
                      style={{ color: `hsl(${hue},80%,88%)`, fontSize: '0.6rem' }}
                    >
                      {getInitials(chip.label)}
                    </span>
                  </span>
                );
              })()
            )}
          </Link>
        );
      })}
    </div>
  );
}
